import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Upload, FileText, Loader2, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

const DOC_TYPES = ['PAN', 'Aadhaar', 'Form 16', 'Bank Statement', 'Capital Gains Statement', 'Investment Proof', 'Other'];

const TaxDocumentUpload = ({ caseId, allowDelete = true }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('PAN');
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` };

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/tax-cases/${caseId}/documents`, { headers });
      setDocs(res.data || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const onPick = () => fileInputRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/tax-cases/${caseId}/documents?document_type=${encodeURIComponent(docType)}`, fd, { headers });
      toast.success(`${docType} uploaded`);
      e.target.value = '';
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/tax-documents/${doc.id}/download`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/tax-documents/${id}`, { headers });
      toast.success('Deleted');
      refresh();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: '#F8FAFC', borderRadius: 10, border: '1px dashed #CBD5E1' }} data-testid={`tax-docs-${caseId}`}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={docType} onChange={(e) => setDocType(e.target.value)} style={{ padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.85rem', background: 'white' }} data-testid={`docs-type-${caseId}`}>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="file" ref={fileInputRef} onChange={onUpload} accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} data-testid={`docs-input-${caseId}`} />
        <button onClick={onPick} disabled={uploading} className="outline-button" style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem' }} data-testid={`docs-upload-${caseId}`}>
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading...</> : <><Upload size={14} /> Upload {docType}</>}
        </button>
        <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>PDF, JPG, PNG • max 10 MB</span>
      </div>
      {docs.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {docs.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', border: '1px solid #E2E8F0', padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem' }} data-testid={`doc-${d.id}`}>
              <FileText size={12} style={{ color: '#10B981' }} />
              <span style={{ color: '#0F172A', fontWeight: 600 }}>{d.document_type}:</span>
              <span style={{ color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.original_filename}</span>
              <button onClick={() => download(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FF6B35' }} title="Download"><Download size={12} /></button>
              {allowDelete && <button onClick={() => remove(d.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }} title="Delete"><Trash2 size={12} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TaxDocumentUpload;
